import { useToast } from '@shared/ui/toast'

import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleStoreApi } from '../api/schedule-store.hook'
import { useLessonRecurringTrackApi } from '../api/lessons/recurring-track.hook'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'

interface ModalDisciplineScheduleCreateConfirmReturn {
  refKidName: ComputedRef<string>
  refIsSaving: ComputedRef<boolean>
  close: () => void
  back: () => void
  confirm: () => Promise<void>
}

/**
 * Creates the lessons with the picked tutor. The modal keeps its loader until the General info tab shows the new
 * schedule; a refused write leaves it open, as in legacy, since a run can be stored only once and a retry needs the
 * admin's eye.
 */
export const useModalDisciplineScheduleCreateConfirm = (): ModalDisciplineScheduleCreateConfirmReturn => {
  const toast = useToast()
  const store = useDisciplineScheduleStore()
  const { refKidName, refDisciplineId, close, back, refreshDiscipline } = useDisciplineScheduleContext()

  const refIsSaving = computed<boolean>(() => store.isSaving)

  const confirm = async (): Promise<void> => {
    if (store.isSaving) return

    const runId = store.getActivationRunId
    const tutorId = store.getChosenTutorId
    const startDate = store.getStartDate
    const days = store.getChosenSlots

    if (!startDate || !days.length || !tutorId || runId === null) {
      toast.warning({ title: 'Warning!', description: 'You need to fill all the required fields.' })
      return
    }

    store.setSaving(true)

    // A not fully available tutor leaves an audit comment first; its failure does not stop the creation.
    if (store.getChosenAvailability < 100) {
      void useLessonRecurringTrackApi({
        disciplineId: refDisciplineId.value,
        payload: { start_date: startDate, days, tutor_id: tutorId, availability: store.getChosenAvailability },
      })
    }

    const isCreated = await useDisciplineScheduleStoreApi({
      disciplineId: refDisciplineId.value,
      activationRunId: runId,
      payload: { tutor_id: tutorId, start_date: startDate, days },
    })

    if (isCreated) await refreshDiscipline()

    store.setSaving(false)
    if (isCreated) store.closeFlow()
  }

  return {
    refKidName,
    refIsSaving,
    close,
    back,
    confirm,
  }
}
